import type { Location as StoreLocation } from "@hobbyshopify/backend-client";
import type { FormEvent } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { LocationEditor, LocationFields } from "../../components/LocationFields";
import { BackButton, FormError, OnboardingLayout, PrimaryButton } from "../../components/onboarding/OnboardingLayout";
import { formatLocationAddress } from "../../core/primitives/location-form";
import { isAwaitingGeocode, isMappable } from "../../core/repositories/locations-repository";
import { OnboardingLocationsActions, OnboardingLocationsState } from "./onboarding-locations-model";

function AddedLocation({ location, onEdit }: { location: StoreLocation; onEdit: () => void }) {
  const mapped = isMappable(location);
  const searching = isAwaitingGeocode(location);
  const address = formatLocationAddress(location);

  // Three states, not two: an address that never resolves must not sit on
  // "Finding on map…" forever pretending something is still happening.
  const badge = mapped
    ? { label: "On the map", className: "badge-accent" }
    : searching
      ? { label: "Finding on map…", className: "badge-warn" }
      : { label: "Not on the map", className: "badge-neutral" };

  return (
    <div className="flex items-center gap-3 rounded-row border border-line px-3.5 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-white">{location.name}</p>
        <p className="text-xs leading-snug text-muted">{address || "No address — not shown on the map"}</p>
        {!mapped && !searching && address && (
          <p className="mt-1 text-[11px] leading-snug text-faint">
            We couldn't find this address yet — we'll keep trying in the background.
          </p>
        )}
      </div>
      <span className={`badge shrink-0 self-start ${badge.className}`}>{badge.label}</span>
      <button type="button" onClick={onEdit} className="btn-ghost shrink-0 self-start">
        Edit
      </button>
    </div>
  );
}

export function OnboardingLocationsPage() {
  const state = useSubtree(OnboardingLocationsState);
  const actions = useSubtree(OnboardingLocationsActions);
  const observer = useObserver();
  const storeId = observer.watch(state.storeId) ?? "";
  const claimWalkthrough = observer.watch(state.claimWalkthrough);
  const locations = observer.watch(state.locations);
  const countries = observer.watch(state.countries);
  const countriesStatus = observer.watch(state.countriesStatus);
  const draft = observer.watch(state.draft);
  const regions = observer.watch(state.regions);
  const errors = observer.watch(state.errors);
  const addForm = observer.watch(state.addForm);
  const editingId = observer.watch(state.editingId);
  const editDraft = observer.watch(state.editDraft);
  const editRegions = observer.watch(state.editRegions);
  const editErrors = observer.watch(state.editErrors);
  const editForm = observer.watch(state.editForm);

  /**
   * Whether the add form is on screen.
   *
   * Open while there are no locations — the form *is* the screen at that point,
   * and hiding the only thing a merchant can do behind a button would be a
   * pointless extra tap. Once one exists it folds away for good: a shop gets one
   * location for now (the server refuses a second), because every merchant
   * screen that names a branch assumes there is one to name.
   */
  const formOpen = locations.length === 0;

  const addLocation = (e: FormEvent) => {
    e.preventDefault();
    void actions.add();
  };

  // The first location is required, so until one exists "Add location" is the
  // primary action of this screen and Continue is the secondary one. They swap
  // emphasis once the list is no longer empty.
  const hasLocation = locations.length > 0;

  return (
    <OnboardingLayout
      step="locations"
      title="Where is your shop?"
      description="An address puts your shop on the map customers use to find you. Without one, walk-ins can't see you."
      storeId={storeId}
      claimWalkthrough={claimWalkthrough}
      footer={
        <div className="flex items-center justify-between gap-3">
          <BackButton onClick={actions.back} />
          <PrimaryButton type="button" onClick={actions.next} disabled={!hasLocation}>
            Continue
          </PrimaryButton>
        </div>
      }
    >
      <FormError message={addForm.error ?? editForm.error} />

      {locations.length > 0 && (
        <ul className="mb-2.5 flex flex-col gap-2.5">
          {locations.map((location) => (
            <li key={location.id}>
              {editingId === location.id ? (
                <LocationEditor
                  draft={editDraft}
                  errors={editErrors}
                  countries={countries}
                  countriesStatus={countriesStatus}
                  regions={editRegions}
                  onChange={actions.setEditField}
                  idPrefix={`onboarding-edit-${location.id}`}
                  submitLabel="Save location"
                  submittingLabel="Saving…"
                  busy={editForm.busy}
                  onSubmit={() => void actions.saveEdit()}
                  onCancel={actions.cancelEdit}
                />
              ) : (
                <AddedLocation location={location} onEdit={() => actions.startEdit(location.id)} />
              )}
            </li>
          ))}
        </ul>
      )}

      {formOpen ? (
        <form onSubmit={addLocation} className="flex flex-col gap-2.5 rounded-row border border-line p-3.5">
          <p className="text-[13px] font-semibold text-white">Add your location</p>

          <LocationFields
            draft={draft}
            errors={errors}
            countries={countries}
            countriesStatus={countriesStatus}
            regions={regions}
            onChange={actions.setField}
            idPrefix="location"
          />

          {/* The screen's primary action while the list is empty, which is the
              only time this form is on screen — Continue stays disabled until
              it succeeds. */}
          <button type="submit" disabled={addForm.busy} className="btn-primary btn-sm mt-0.5 w-full">
            {addForm.busy ? "Adding…" : "+ Add location"}
          </button>
        </form>
      ) : null}

      {!hasLocation && (
        <p className="mt-3 text-pretty text-[11.5px] leading-relaxed text-faint">
          Add at least one location to continue — a shop with no mapped address never appears to walk-in customers.
        </p>
      )}
    </OnboardingLayout>
  );
}
